import { AUTO_GROUP_NAMES } from '../stores/utils/auto-agent-grouping';

/**
 * The one place an agent group's colors are worked out, for every Bobcat
 * surface that draws an agent: the Build canvas cards, Table View's group
 * rows, and the Design page's agent pills.
 *
 * From the CS review, via Mostafa's prototype
 * (`prostudio-build-flow-layouts.html`, "Group colours: soft"): agent colors
 * should still tell groups apart but stop shouting. So a Use-case agent's
 * header is a light tint of its color with the name in a deep shade of the
 * same hue, and the full color survives only as a 3px edge. The Supervisor
 * and the Knowledge Agent (the two every solution has one of) keep their
 * solid fills with white text.
 */

export type AgentCategory = 'core' | 'use-case';

const CORE_GROUP_NAMES = new Set(
  AUTO_GROUP_NAMES.map((name) => name.toLowerCase())
);

/**
 * `core` is exactly the two names auto-agent-grouping creates ("Supervisor
 * Agent", "Knowledge Agent"). Everything else, a real Use Case Agent group
 * or an untitled one, is `use-case`.
 */
export const agentCategoryFor = (
  name: string | null | undefined
): AgentCategory =>
  CORE_GROUP_NAMES.has((name ?? '').trim().toLowerCase()) ? 'core' : 'use-case';

/** The fallback when a group has no color yet (the prototype's `#62697c`). */
export const AGENT_FALLBACK_COLOR = '#62697c';

/** Near-black the soft text shade is mixed toward (`#151a25`, gray 500). */
const INK = '#151a25';
const WHITE = '#ffffff';

type Rgb = { r: number; g: number; b: number };

const parseHex = (hex: string | null | undefined): Rgb | null => {
  const m = hex ? /^#?([0-9a-f]{6})$/i.exec(hex.trim()) : null;
  if (!m) return null;
  return {
    r: parseInt(m[1].slice(0, 2), 16),
    g: parseInt(m[1].slice(2, 4), 16),
    b: parseInt(m[1].slice(4, 6), 16),
  };
};

const toHex = ({ r, g, b }: Rgb): string => {
  const hx = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${hx(r)}${hx(g)}${hx(b)}`;
};

// Relative luminance per WCAG, for 8-bit sRGB.
const luminance = ({ r, g, b }: Rgb): number => {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

/**
 * `color-mix(in srgb, a <weight>, b)`: `weight` of `a`, the rest `b`. Returns
 * `null` if either color isn't a 6-digit hex.
 */
export const mixHex = (a: string, b: string, weight: number): string | null => {
  const x = parseHex(a);
  const y = parseHex(b);
  if (!x || !y) return null;
  return toHex({
    r: x.r * weight + y.r * (1 - weight),
    g: x.g * weight + y.g * (1 - weight),
    b: x.b * weight + y.b * (1 - weight),
  });
};

/**
 * The group's color, darkened in steps until white text on it clears 4.5:1.
 * `groupColor` is a user-picked, unconstrained hex (PSTUDIO-2220/2349), and
 * the gold use-case preset (#FFA800) is too light for white. The prototype
 * solves the same problem with a palette that is "dark enough for white
 * text, everywhere". Returns `null` for a missing or unparseable color.
 */
export const readableAgentColor = (
  hex: string | null | undefined
): string | null => {
  let rgb = parseHex(hex);
  if (!rgb) return null;
  for (let i = 0; i < 20 && 1.05 / (luminance(rgb) + 0.05) < 4.5; i += 1) {
    rgb = {
      r: Math.round(rgb.r * 0.9),
      g: Math.round(rgb.g * 0.9),
      b: Math.round(rgb.b * 0.9),
    };
  }
  return toHex(rgb);
};

export interface AgentPalette {
  category: AgentCategory;
  /** True for a Use-case agent: tinted fill, deep text, a solid edge. */
  soft: boolean;
  /** The agent's full, white-text-safe color. */
  solid: string;
  /** Header / band / pill fill. */
  fill: string;
  /** Text on `fill`. */
  text: string;
  /** The 3px edge a soft agent keeps in its full color; `null` when solid. */
  edge: string | null;
  /** A slightly stronger tint, for a soft agent's body behind its chips. */
  bodyTint: string | null;
}

/**
 * Everything a surface needs to paint one agent. `color` is the group's
 * `settings.groupColor` (or any default the caller already applied).
 */
export const agentPaletteFor = ({
  color,
  category,
}: {
  color: string | null | undefined;
  category: AgentCategory;
}): AgentPalette => {
  const solid = readableAgentColor(color) ?? AGENT_FALLBACK_COLOR;
  if (category === 'core') {
    return {
      category,
      soft: false,
      solid,
      fill: solid,
      text: WHITE,
      edge: null,
      bodyTint: null,
    };
  }
  return {
    category,
    soft: true,
    solid,
    fill: mixHex(solid, WHITE, 0.12) ?? solid,
    text: mixHex(solid, INK, 0.7) ?? INK,
    edge: solid,
    bodyTint: mixHex(solid, WHITE, 0.14),
  };
};

/**
 * Set on <html> while the five-stage nav is on (pages/builder.tsx). The soft
 * agent colors in the canvas and Table SCSS apply only under it.
 */
export const BOBCAT_POLISH_CLASS = 'bobcat-polish';
